// src/Payroll_Management/PayRoll_Report/EmployeeResignationReport.jsx
import { useEffect, useContext, useMemo, useState, useRef } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  RefreshCcw,
  ChevronDown,
  ChevronUp,
  Hash,
  Type,
  Building,
  Layers,
  BadgeCheck,
  Wallet,
  Calendar,
  Download,
  Printer,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

function s(v) {
  return (v ?? "").toString().trim();
}

// simple debounce
function useDebouncedValue(value, delay = 350) {
  const [deb, setDeb] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDeb(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return deb;
}

function toCsv(rows) {
  if (!rows || rows.length === 0) return "";
  const cols = Object.keys(rows[0]);
  const esc = (x) => {
    const v = x == null ? "" : String(x);
    const needs = /[",\n]/.test(v);
    const vv = v.replace(/"/g, '""');
    return needs ? `"${vv}"` : vv;
  };
  const header = cols.map(esc).join(",");
  const body = rows.map((r) => cols.map((c) => esc(r[c])).join(",")).join("\n");
  return header + "\n" + body;
}

function downloadTextFile(filename, content, mime = "text/plain;charset=utf-8") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const EmployeeResignationReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";

  const [loading, setLoading] = useState(false);

  // meta
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [grades, setGrades] = useState([]);
  const [designations, setDesignations] = useState([]);

  // filters
  const [showFilters, setShowFilters] = useState(true);

  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");

  const [branchId, setBranchId] = useState("Select");
  const [deptId, setDeptId] = useState("Select");
  const [gradeId, setGradeId] = useState("Select");
  const [desigId, setDesigId] = useState("Select");
  const [pfNonPf, setPfNonPf] = useState("Select");
  const [payMode, setPayMode] = useState("Select");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [resignDate, setResignDate] = useState("");

  // autocomplete
  const [empCodeSug, setEmpCodeSug] = useState([]);
  const [empNameSug, setEmpNameSug] = useState([]);
  const debEmpCode = useDebouncedValue(empCode, 300);
  const debEmpName = useDebouncedValue(empName, 300);
  const lastAutoFetchRef = useRef({ code: "", name: "" });

  // list
  const [rows, setRows] = useState([]);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-54", []);
  const titleText = useMemo(() => "EMPLOYEE RESIGNATION REPORT", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark ? "border-white/10 text-white hover:bg-white/10" : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const disabledBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    "bg-gray-400 text-white cursor-not-allowed";

  const fetchMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(loginBranchId).trim() || !String(branchType).trim()) {
      toast.warn("Missing login branch/session (branchId / branchType).");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-resignation-report/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId),
          branchType: s(branchType),
        },
      });

      if (res?.data?.success) {
        setBranches(res.data.branches || []);
        setDepartments(res.data.departments || []);
        setGrades(res.data.grades || []);
        setDesignations(res.data.designations || []);

        const defBr = res.data.defaultBranchId ? String(res.data.defaultBranchId) : "Select";
        setBranchId(defBr || "Select");
      } else {
        toast.error(res?.data?.message || "Meta load failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta load failed");
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (p = 1) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/employee-resignation-report/search`, {
        params: {
          finYearId: Number(finYearId),
          loginBranchId: Number(loginBranchId),

          branchId,
          siteId: branchId, // same as ASP "site"
          empCode: empCode.trim(),
          empName: empName.trim(),
          deptId,
          desigId,
          gradeId,
          pfNonPf,
          payMode,
          fromDate: fromDate.trim(),
          toDate: toDate.trim(),
          resignDate: resignDate.trim(),

          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Search failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Autocomplete calls
  const fetchEmpCodeAuto = async (prefix) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/employee-resignation-report/autocomplete/empcode`, {
        params: {
          finYearId: Number(finYearId),
          prefix,
          branchId: branchId === "Select" ? String(loginBranchId) : String(branchId),
          branchType: s(branchType),
        },
      });
      if (res?.data?.success) setEmpCodeSug(res.data.items || []);
    } catch {
      // silent
    }
  };

  const fetchEmpNameAuto = async (prefix) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/employee-resignation-report/autocomplete/empname`, {
        params: {
          finYearId: Number(finYearId),
          prefix,
          branchId: branchId === "Select" ? String(loginBranchId) : String(branchId),
          branchType: s(branchType),
        },
      });
      if (res?.data?.success) setEmpNameSug(res.data.items || []);
    } catch {
      // silent
    }
  };

  // initial load = meta + first grid (ASP does this on first load)
  useEffect(() => {
    (async () => {
      await fetchMeta();
      await fetchList(1);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // paging changes
  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // autocomplete hooks
  useEffect(() => {
    if (!String(finYearId).trim()) return;
    if (!debEmpCode || debEmpCode.length < 1) {
      setEmpCodeSug([]);
      return;
    }
    const key = `${branchId}|${debEmpCode}`;
    if (lastAutoFetchRef.current.code === key) return;
    lastAutoFetchRef.current.code = key;
    fetchEmpCodeAuto(debEmpCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debEmpCode, branchId]);

  useEffect(() => {
    if (!String(finYearId).trim()) return;
    if (!debEmpName || debEmpName.length < 1) {
      setEmpNameSug([]);
      return;
    }
    const key = `${branchId}|${debEmpName}`;
    if (lastAutoFetchRef.current.name === key) return;
    lastAutoFetchRef.current.name = key;
    fetchEmpNameAuto(debEmpName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debEmpName, branchId]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleReset = async () => {
    setEmpCode("");
    setEmpName("");
    setDeptId("Select");
    setGradeId("Select");
    setDesigId("Select");
    setPfNonPf("Select");
    setPayMode("Select");
    setFromDate("");
    setToDate("");
    setResignDate("");

    setPage(1);
    await fetchList(1);
  };

  const handleExport = async () => {
    if (total <= 0) {
      toast.warn("No records to export");
      return;
    }
    try {
      setLoading(true);
     const res = await axios.get(`${BASE_URL}/api/employee-resignation-report/search`, {
  params: {
    finYearId: Number(finYearId),
    loginBranchId: Number(loginBranchId),

    branchId,
    siteId: branchId,
    empCode: empCode.trim(),
    empName: empName.trim(),
    deptId,
    desigId,
    gradeId,
    pfNonPf,
    payMode,
    fromDate: fromDate.trim(),
    toDate: toDate.trim(),
    resignDate: resignDate.trim(),

    export: 1, // ✅ new
  },
});


      if (res?.data?.success) {
        const exportRows = res.data.rows || [];
        const withSno = exportRows.map((r, idx) => ({ "S No": idx + 1, ...r }));
        const csv = toCsv(withSno);
        const filename = `EmployeeResignationReport_${new Date().toISOString().slice(0, 10)}.csv`;
        downloadTextFile(filename, csv, "text/csv;charset=utf-8");
        toast.success("Export downloaded");
      } else {
        toast.error(res?.data?.message || "Export failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  const handlePrintAll = () => {
    if (total <= 0) {
      toast.warn("No records to print");
      return;
    }
    const params = new URLSearchParams({
      finYearId: String(finYearId),
      loginBranchId: String(loginBranchId),
      branchId: String(branchId),
      deptId: String(deptId),
      desigId: String(desigId),
      empCode: empCode.trim(),
      empName: empName.trim(),
      payMode: String(payMode),
      pfNonPf: String(pfNonPf),
      fromDate: fromDate.trim(),
      toDate: toDate.trim(),
      resignDate: resignDate.trim(),
    }).toString();

    window.open(`/PayRoll/EmployeeResignationAllReportPrint?${params}`, "_blank");
  };

  const handlePrintEmployee = (employeeId) => {
    if (!employeeId) return;
    window.open(`/PayRoll/EmployeeResignReportPrint?empId=${employeeId}`, "_blank");
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">{titleText}</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Employee Resignation Report
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search / Export / Print / Pagination
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Employee Code */}
                <div>
                  <label className={labelBase}>Employee Code</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <div className="w-full relative">
                      <input
                        value={empCode}
                        onChange={(e) => setEmpCode(e.target.value)}
                        placeholder="Type employee code..."
                        className={inputBase}
                        list="empCodeSug"
                      />
                      <datalist id="empCodeSug">
                        {empCodeSug.map((x, i) => (
                          <option key={i} value={x} />
                        ))}
                      </datalist>
                    </div>
                  </div>
                </div>

                {/* Employee Name */}
                <div>
                  <label className={labelBase}>Employee Name</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Type className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <div className="w-full relative">
                      <input
                        value={empName}
                        onChange={(e) => setEmpName(e.target.value)}
                        placeholder="Type employee name..."
                        className={inputBase}
                        list="empNameSug"
                      />
                      <datalist id="empNameSug">
                        {empNameSug.map((x, i) => (
                          <option key={i} value={x} />
                        ))}
                      </datalist>
                    </div>
                  </div>
                </div>

                {/* Branch */}
                <div>
                  <label className={labelBase}>Branch</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Building className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select
                      className={inputBase}
                      value={branchId}
                      onChange={(e) => {
                        setBranchId(e.target.value);
                        setEmpCodeSug([]);
                        setEmpNameSug([]);
                      }}
                    >
                      <option value="Select">Select</option>
                      {branches.map((b) => (
                        <option key={b.fld_branchid} value={String(b.fld_branchid)}>
                          {b.fld_branchname}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Department */}
                <div>
                  <label className={labelBase}>Department</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Layers className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select className={inputBase} value={deptId} onChange={(e) => setDeptId(e.target.value)}>
                      <option value="Select">Select</option>
                      {departments.map((d) => (
                        <option key={d.fld_deptid} value={String(d.fld_deptid)}>
                          {d.fld_departmentname}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Grade */}
                <div>
                  <label className={labelBase}>Grade</label>
                  <div className="mt-2 flex items-center gap-2">
                    <BadgeCheck className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select className={inputBase} value={gradeId} onChange={(e) => setGradeId(e.target.value)}>
                      <option value="Select">Select</option>
                      {grades.map((g) => (
                        <option key={g.fld_gradeid} value={String(g.fld_gradeid)}>
                          {g.fld_gradename}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Designation */}
                <div>
                  <label className={labelBase}>Designation</label>
                  <div className="mt-2 flex items-center gap-2">
                    <BadgeCheck className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select className={inputBase} value={desigId} onChange={(e) => setDesigId(e.target.value)}>
                      <option value="Select">Select</option>
                      {designations.map((d) => (
                        <option key={d.fld_desigid} value={String(d.fld_desigid)}>
                          {d.fld_designationname}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* PF/Non PF */}
                <div>
                  <label className={labelBase}>PF / Non PF</label>
                  <div className="mt-2 flex items-center gap-2">
                    <BadgeCheck className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select className={inputBase} value={pfNonPf} onChange={(e) => setPfNonPf(e.target.value)}>
                      <option value="Select">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>

                {/* Payment Mode */}
                <div>
                  <label className={labelBase}>Payment Mode</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Wallet className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select className={inputBase} value={payMode} onChange={(e) => setPayMode(e.target.value)}>
                      <option value="Select">Select</option>
                      <option value="Cash">Cash</option>
                      <option value="Bank">Bank</option>
                    </select>
                  </div>
                </div>

                {/* From Date */}
                <div>
                  <label className={labelBase}>From Date (dd/MM/yyyy)</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Calendar className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      placeholder="dd/MM/yyyy"
                      className={inputBase}
                    />
                  </div>
                </div>

                {/* To Date */}
                <div>
                  <label className={labelBase}>To Date (dd/MM/yyyy)</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Calendar className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      placeholder="dd/MM/yyyy"
                      className={inputBase}
                    />
                  </div>
                </div>

                {/* Resignation Date */}
                <div>
                  <label className={labelBase}>Resignation Date (dd/MM/yyyy)</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Calendar className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input
                      value={resignDate}
                      onChange={(e) => setResignDate(e.target.value)}
                      placeholder="dd/MM/yyyy"
                      className={inputBase}
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>

                <button type="button" onClick={handleReset} className={ghostBtn} disabled={loading}>
                  <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                  Reset
                </button>

                <button type="button" onClick={handleExport} className={total > 0 ? ghostBtn : disabledBtn} disabled={loading || total <= 0}>
                  <Download className="w-4 h-4" />
                  Excel
                </button>

                <button type="button" onClick={handlePrintAll} className={total > 0 ? ghostBtn : disabledBtn} disabled={loading || total <= 0}>
                  <Printer className="w-4 h-4" />
                  Print
                </button>
              </div>
            </form>
          )}

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Designation</th>
                    <th className="px-4 py-4 text-center font-bold">PF No.</th>
                    <th className="px-4 py-4 text-center font-bold">Qualification</th>
                    <th className="px-4 py-4 text-center font-bold">Date of Joining</th>
                    <th className="px-4 py-4 text-center font-bold">Last Modified Date</th>
                    <th className="px-4 py-4 text-center font-bold">Resign Date</th>
                    <th className="px-4 py-4 text-center font-bold">Gross Salary</th>
                    <th className="px-4 py-4 text-center font-bold">Batta</th>
                    <th className="px-4 py-4 text-center font-bold">Grand Total</th>
                    <th className="px-4 py-4 text-center font-bold">Special HRA</th>
                    <th className="px-4 py-4 text-center font-bold">PF</th>
                    <th className="px-4 py-4 text-center font-bold">Location</th>
                    <th className="px-4 py-4 text-center font-bold">Print</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 16 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={16} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={`${r.fld_Employeeid ?? idx}-${idx}`} className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_Employeecode}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_Employee}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_designationname}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_pfno}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_qualificationname}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.DOJ}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.modifieddate}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.resigndate}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_grosssalary}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_dailybata}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.grandTot}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_SpecialHRA}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_pf}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_BranchName}</td>
                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={() => handlePrintEmployee(r.fld_Employeeid)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-2xl hover:bg-indigo-700 transform hover:-translate-y-0.5 transition-all shadow"
                          >
                            <Printer className="w-4 h-4" />
                            Print
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom row: pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {totalPages > 1 && (
                <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        page === i + 1
                          ? `${primaryBtnClass} text-white`
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeResignationReport;
